import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, AlertTriangle, CalendarClock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../../api/axios';
import Modal from '../common/Modal';
import VacationRangePicker from '../common/VacationRangePicker';
import { formatVacationRange, rangeDays } from '../../constants/vacationRange';

// "Mon 12 Oct – Sun 18 Oct 2026 (7 days, approved)"
export function rangeWithStatus(req) {
  return formatVacationRange(req.start_date, req.end_date).replace(/\)$/, `, ${req.status})`);
}

// Months of the range before the current month → "September 2026 and October 2026"
function pastMonthLabels(start, end) {
  const current = format(new Date(), 'yyyy-MM');
  const out = [];
  let d = parseISO(`${start.slice(0, 7)}-01`);
  while (format(d, 'yyyy-MM') <= end.slice(0, 7)) {
    if (format(d, 'yyyy-MM') < current) out.push(format(d, 'MMMM yyyy'));
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return out;
}
function joinLabels(list) {
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

const btn = {
  minHeight: '36px', padding: '6px 12px', fontSize: '0.82rem',
  display: 'inline-flex', alignItems: 'center', gap: '5px',
};

// Edit / Delete buttons for one request (admins only) — large enough to tap on a phone
export function AdminRequestActions({ req, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
      <button type="button" className="btn btn-secondary" style={btn} onClick={() => onEdit(req)} aria-label="Edit vacation">
        <Pencil size={14} /> Edit
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ ...btn, color: 'var(--danger)', borderColor: 'var(--danger)' }}
        onClick={() => onDelete(req)}
        aria-label="Delete vacation"
      >
        <Trash2 size={14} /> Delete
      </button>
    </div>
  );
}

export function DeleteVacationModal({ req, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!req) return null;
  const past = pastMonthLabels(req.start_date, req.end_date);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/vacation-requests/${req.id}`);
      toast.success('Vacation deleted.');
      onDone(res.data, req);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Delete vacation" size="md">
      <p style={{ fontSize: '0.92rem', marginBottom: '12px' }}>
        Delete <strong>{req.user_name}</strong>&apos;s vacation <strong>{rangeWithStatus(req)}</strong>?
      </p>
      {past.length > 0 && (
        <div style={{
          display: 'flex', gap: '8px', padding: '10px 12px', marginBottom: '12px', borderRadius: 'var(--radius)',
          background: 'var(--warning-light)', border: '1px solid var(--warning)', fontSize: '0.85rem',
        }}>
          <AlertTriangle size={16} style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }} />
          <span>
            Part of this vacation is in {joinLabels(past)}. If that month&apos;s payroll was already exported,
            the payroll office needs to be informed.
          </span>
        </div>
      )}
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
        This cannot be undone. The days count again for the yearly limit, and {req.user_name} is notified.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary" style={{ minHeight: '40px' }} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ minHeight: '40px', background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
          onClick={confirm}
          disabled={busy}
        >
          {busy ? 'Deleting…' : 'Delete vacation'}
        </button>
      </div>
    </Modal>
  );
}

export function EditVacationModal({ req, onClose, onDone }) {
  const [entry, setEntry] = useState({ start_date: '', length_days: 7 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (req) {
      setEntry({ start_date: req.start_date, length_days: rangeDays(req.start_date, req.end_date) });
      setError('');
    }
  }, [req]);

  if (!req) return null;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.put(`/vacation-requests/${req.id}`, entry);
      toast.success('Vacation updated.');
      onDone(res.data, req);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Edit ${req.user_name}'s vacation`} size="md">
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '14px' }}>
        Currently: {rangeWithStatus(req)}. The status stays as it is. Overlaps and the 28-day limit are checked;
        past dates are allowed.
      </p>
      <VacationRangePicker
        startDate={entry.start_date}
        lengthDays={entry.length_days}
        onChange={setEntry}
        disabled={busy}
      />
      {error && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: 'var(--radius)', background: 'var(--danger-light)', color: 'var(--danger)', fontSize: '0.83rem' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary" style={{ minHeight: '40px' }} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" style={{ minHeight: '40px' }} onClick={save} disabled={busy || !entry.start_date}>
          {busy ? 'Saving…' : 'Save dates'}
        </button>
      </div>
    </Modal>
  );
}

// Shown after a delete/edit when shifts need cover or a draft should be regenerated
export function VacationChangeResultModal({ result, onClose }) {
  if (!result) return null;
  const { conflicts = [], draft_plans: drafts = [], personName } = result;
  const fmtDay = (d) => format(parseISO(d), 'EEE d MMM yyyy');
  return (
    <Modal isOpen onClose={onClose} title="Roster check" size="md">
      {conflicts.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' }}>
            <AlertTriangle size={17} style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }} />
            <p style={{ fontWeight: 600, fontSize: '0.9rem', margin: 0 }}>
              These shifts now fall into {personName}&apos;s vacation and need cover:
            </p>
          </div>
          <ul style={{ margin: '0 0 10px', paddingLeft: '20px', fontSize: '0.86rem', lineHeight: 1.7 }}>
            {conflicts.map((c) => (
              <li key={c.id}>
                {fmtDay(c.date)} · {c.start_time}–{c.end_time} · {c.shift_name}{c.kind === 'event' ? ' (event)' : ''}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Nothing was unassigned automatically.
          </p>
          <Link to="/roster" className="btn btn-primary" style={{ minHeight: '40px', display: 'inline-flex', alignItems: 'center' }}>
            Open roster
          </Link>
        </div>
      )}
      {drafts.length > 0 && (
        <div style={{
          display: 'flex', gap: '8px', padding: '10px 12px', borderRadius: 'var(--radius)',
          background: 'var(--primary-xlight)', border: '1px solid var(--primary)', fontSize: '0.85rem', marginBottom: '12px',
        }}>
          <CalendarClock size={16} style={{ color: 'var(--primary-dark)', flexShrink: 0, marginTop: '2px' }} />
          <span>
            Regenerate the draft to apply the change:{' '}
            {drafts.map((d) => `${fmtDay(d.start_date)} – ${fmtDay(d.end_date)}`).join(', ')}.
          </span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" style={{ minHeight: '40px' }} onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

// Admins: all requests of one person in a year, every status
export function PersonRequestsCard({ users, year, reloadKey, onEdit, onDelete, StatusBadge }) {
  const [userId, setUserId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!userId) { setRows([]); return; }
    setLoading(true);
    api.get(`/vacation-requests?user_id=${userId}&year=${year}`)
      .then((res) => setRows((res.data || []).slice().sort((a, b) => a.start_date.localeCompare(b.start_date))))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [userId, year]);

  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, flex: '1 1 160px' }}>Requests by person</h2>
        <select
          className="form-input"
          style={{ flex: '1 1 200px', maxWidth: '320px', minHeight: '40px' }}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          aria-label="Person"
        >
          <option value="">Choose a person…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      {!userId ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
          Choose a person to see all their requests in {year} (pending, approved and rejected).
        </p>
      ) : loading ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>No requests in {year}.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {rows.map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border)',
              }}
            >
              <span style={{ flex: '1 1 200px', fontSize: '0.87rem', fontWeight: 600 }}>
                {formatVacationRange(r.start_date, r.end_date)}
              </span>
              <StatusBadge status={r.status} />
              <AdminRequestActions req={r} onEdit={onEdit} onDelete={onDelete} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
